import React, { useCallback, useEffect, useRef, useState } from "react";
import { apptApi, invoiceApi, resultApi, staffApi } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Modal, Button, Badge, StatusBadge, Section, ReasonModal, Loading } from "../../Components/UI/ui";
import { GATE, STATUS_LABEL, RESULT_STATUS_LABEL, TERMINAL, nextMoves, money, fmtDateTime, fmtTime, cap, notificationLine } from "../../lib/lab";
import AppointmentForm from "./AppointmentForm";
import StudiesPanel from "../Radiology/StudiesPanel";
import PaymentModal from "../Billing/PaymentModal";
import ResultSheet from "../Results/ResultSheet";
import PrintReportButton from "../../Components/Print/PrintReportButton";
import SpecimenPanel from "./SpecimenPanel";
import NotifyPatient from "./NotifyPatient";

// Everything you can do to one booking: move it through the lifecycle,
// assign a collector, attach/publish reports, bill and take payment.
const AppointmentDetail = ({ id, open, onClose, onChanged }) => {
  const { can } = useAuth();
  const { toast, templates, loadTemplates } = useApp();
  const [appt, setAppt] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [reasonFor, setReasonFor] = useState(null); // status needing a reason
  const [editOpen, setEditOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [collector, setCollector] = useState("");
  const [members, setMembers] = useState([]);       // for the collector picker
  const [printKey, setPrintKey] = useState(0);      // bump after publish to refresh print status
  const [specKey, setSpecKey] = useState(0);        // bump after a status move to refresh tubes
  const [notifyKey, setNotifyKey] = useState(0);    // bump when a status move sends a confirmation
  const [spec, setSpec] = useState(null);           // latest specimen state from the panel
  // Parents pass inline callbacks; keep them in refs so `load` only changes with `id`
  const onCloseRef = useRef(onClose); onCloseRef.current = onClose;
  const onChangedRef = useRef(onChanged); onChangedRef.current = onChanged;

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const a = await apptApi.get(id);
      setAppt(a);
      setCollector(a.homeCollection?.phlebotomistStaffId || "");
      if (a.invoiceId) {
        try { setInvoice(await invoiceApi.get(a.invoiceId)); } catch { setInvoice(null); }
      } else setInvoice(null);
      if (a.serviceType === "pathology") {
        loadTemplates();
        try { setSheets((await resultApi.list({ appointmentId: a._id, limit: 50 })).results || []); } catch { setSheets([]); }
      } else setSheets([]);
      if (a.collectionType === "home" && !TERMINAL.has(a.status)) {
        try {
          const res = await staffApi.list({ isActive: true });
          // phlebotomists first, then everyone else
          setMembers([...(res?.data || [])].sort((x, y) => (x.staffType === "phlebotomist" ? -1 : 0) - (y.staffType === "phlebotomist" ? -1 : 0)));
        } catch { setMembers([]); }
      }
    } catch (e) {
      toast.error(e);
      onCloseRef.current?.();
    } finally {
      setLoading(false);
    }
  }, [id, toast, loadTemplates]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const applied = (a) => { setAppt(a); onChangedRef.current?.(a); };

  const syncSheets = async () => {
    setBusy("sync");
    try {
      const res = await resultApi.init(appt._id);
      setSheets(res.results || []);
      const missing = res.missingTemplates || [];
      toast.success(`${res.created?.length ?? 0} sheet(s) created${missing.length ? ` · no definition for ${missing.map((m) => m.name || m.skuName || m).join(", ")}` : ""}`);
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  // A published sheet changes the booking (reports[], possibly REPORT_READY)
  const sheetChanged = async (s) => {
    setSheets((xs) => xs.map((x) => (x._id === s._id ? { ...x, ...s, templateSnapshot: undefined, values: undefined } : x)));
    if (s.status === "PUBLISHED") { setPrintKey((k) => k + 1); load(); }
  };

  const move = async (status, reason) => {
    setBusy(status);
    try {
      const a = await apptApi.setStatus(appt._id, { status, reason, note: reason });
      applied(a);
      setSpecKey((k) => k + 1);
      toast.success(`Marked ${STATUS_LABEL[status].toLowerCase()}`);
      // Accepting a referral (REQUESTED → CONFIRMED) WhatsApps the patient
      const c = a?.confirmation;
      if (c) {
        setNotifyKey((k) => k + 1);
        if (c.status === "sent") toast.success(notificationLine(c));
        else toast.error(`${notificationLine(c)} — use "Retry confirmation".`);
      }
      if (status === "CANCELLED" && a.invoiceId) load();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null); setReasonFor(null);
    }
  };

  const assignCollector = async () => {
    if (!collector.trim()) return;
    setBusy("collector");
    try {
      applied(await apptApi.setCollector(appt._id, collector.trim()));
      toast.success("Collector assigned");
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const createInvoice = async () => {
    setBusy("invoice");
    try {
      const inv = await invoiceApi.fromAppointment(appt._id);
      setInvoice(inv);
      const a = await apptApi.get(appt._id);
      applied(a);
      toast.success(`Invoice ${inv.invoiceNumber} created`);
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this booking? This cannot be undone.")) return;
    setBusy("delete");
    try {
      await apptApi.remove(appt._id);
      toast.success("Booking deleted");
      onChangedRef.current?.(null);
      onCloseRef.current?.();
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  };

  // "Sample collected" is only offered once every tube has a sticker (the server refuses otherwise)
  const moves = appt ? nextMoves(appt).filter((m) => can(...m.roles)).filter((m) => m.status !== "SAMPLE_COLLECTED" || appt.serviceType !== "pathology" || spec?.allCollected) : [];
  const editable = appt && ["REQUESTED", "CONFIRMED"].includes(appt.status) && can(...GATE.book);
  const canBill = appt && !appt.invoiceId && !["CANCELLED", "NO_SHOW"].includes(appt.status) && can(...GATE.invoiceWrite);
  const canPay = invoice && !["Paid", "Cancelled"].includes(invoice.status) && can(...GATE.invoiceWrite);

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        size="lg"
        title={appt ? `${appt.patientName}` : "Booking"}
        footer={
          appt && (
            <>
              {can(...GATE.deleteAppointment) && ["REQUESTED", "CONFIRMED", "CANCELLED"].includes(appt.status) && !appt.invoiceId && (
                <Button variant="danger" size="sm" loading={busy === "delete"} onClick={remove} style={{ marginRight: "auto" }}>Delete</Button>
              )}
              {editable && <Button onClick={() => setEditOpen(true)}>Edit booking</Button>}
              {moves.map((m) => (
                <Button
                  key={m.status}
                  variant={m.status === "CANCELLED" || m.status === "NO_SHOW" ? "danger" : m.status === "COMPLETED" ? "success" : "navy"}
                  loading={busy === m.status}
                  onClick={() => (m.reason ? setReasonFor(m.status) : move(m.status))}
                >
                  {m.status === "COMPLETED" ? "Mark completed" : `Mark ${STATUS_LABEL[m.status].toLowerCase()}`}
                </Button>
              ))}
            </>
          )
        }
      >
        {loading || !appt ? (
          <Loading />
        ) : (
          <>
            <div className="lab-row between">
              <div className="lab-row">
                <StatusBadge status={appt.status} />
                <Badge kind={appt.serviceType}>{cap(appt.serviceType)}</Badge>
                {appt.collectionType === "home" && <Badge kind="home">Home collection</Badge>}
                {appt.source === "doctor_referral" && <Badge kind="REQUESTED">Referral</Badge>}
                <Badge kind={appt.paymentStatus}>{cap(appt.paymentStatus || "unpaid")}</Badge>
                {appt.serviceType === "pathology" && (appt.sampleId || spec?.sampleId
                  ? <span className="lab-sample-id" style={{ fontSize: 14 }} title="Sample ID (accession number)">{spec?.sampleId || appt.sampleId}</span>
                  : <span className="lab-muted">no sample yet</span>)}
                {(spec?.specimens || []).filter((t) => !t.replacedBy).map((t) => (
                  <span key={t.code} className={`lab-tube ${t.code.replace(/\d+$/, "")}`} style={{ height: 24, fontSize: 12, gap: 4 }} title={t.specimenType}>
                    {t.code}{t.status === "collected" ? ` ✓ ${t.label}` : t.status === "rejected" ? " ✕" : " · pending"}
                  </span>
                ))}
              </div>
              <div className="lab-muted">{fmtDateTime(appt.scheduledAt)} · {appt.durationMinutes} min</div>
            </div>
            <NotifyPatient appointment={appt} refreshKey={notifyKey} />
            {appt.serviceType === "pathology" && <PrintReportButton appointmentId={appt._id} refreshKey={printKey} />}

            <div className="lab-grid cols-2">
              <Section title="Patient">
                <dl className="lab-kv">
                  <dt>Name</dt><dd>{appt.patientName}</dd>
                  <dt>Phone</dt><dd>{appt.patientPhone || "—"}</dd>
                  {appt.referral?.doctorName && (<><dt>Referred by</dt><dd>{appt.referral.doctorName}{appt.referral.clinicName ? ` · ${appt.referral.clinicName}` : ""}</dd></>)}
                  {appt.referral?.notes && (<><dt>Referral notes</dt><dd>{appt.referral.notes}</dd></>)}
                  {appt.notes && (<><dt>Notes</dt><dd>{appt.notes}</dd></>)}
                  {appt.thingsToRemember?.length > 0 && (<><dt>Remember</dt><dd>{appt.thingsToRemember.join(", ")}</dd></>)}
                  <dt>Booked by</dt><dd>{appt.createdBy?.name || "—"}</dd>
                </dl>
              </Section>

              {appt.collectionType === "home" ? (
                <Section title="Home collection">
                  <dl className="lab-kv">
                    <dt>Address</dt>
                    <dd>{[appt.homeCollection?.address?.addressLine1, appt.homeCollection?.address?.addressLine2, appt.homeCollection?.address?.city, appt.homeCollection?.address?.postalCode].filter(Boolean).join(", ") || "—"}</dd>
                    <dt>Window</dt>
                    <dd>{appt.homeCollection?.windowStart ? `${fmtTime(appt.homeCollection.windowStart)} – ${fmtTime(appt.homeCollection.windowEnd)}` : "—"}</dd>
                    <dt>Collector</dt><dd>{appt.homeCollection?.phlebotomistName || <span className="lab-muted">Unassigned</span>}</dd>
                    {appt.homeCollection?.collectedAt && (<><dt>Collected at</dt><dd>{fmtDateTime(appt.homeCollection.collectedAt)}</dd></>)}
                  </dl>
                  {!TERMINAL.has(appt.status) && can(...GATE.book) && (
                    <div className="lab-row" style={{ marginTop: 10 }}>
                      <select className="lab-select sm" style={{ flex: 1 }} value={collector} onChange={(e) => setCollector(e.target.value)}>
                        <option value="">Assign a collector…</option>
                        {members.map((m) => <option key={m._id} value={m._id}>{[m.name?.firstName, m.name?.lastName].filter(Boolean).join(" ")} · {cap(m.staffType)}</option>)}
                      </select>
                      <Button size="sm" disabled={!collector} loading={busy === "collector"} onClick={assignCollector}>Assign</Button>
                    </div>
                  )}
                </Section>
              ) : (
                <Section title="Items">
                  <ItemsTable appt={appt} />
                </Section>
              )}
            </div>

            {appt.collectionType === "home" && (
              <Section title="Items"><ItemsTable appt={appt} /></Section>
            )}

            {appt.serviceType === "pathology" && (
              <SpecimenPanel appointment={appt} refreshKey={specKey} onAppointmentChanged={() => load()} onState={setSpec} />
            )}

            {appt.serviceType === "radiology" && (
              <StudiesPanel appointment={appt} refreshKey={specKey} onAppointmentChanged={() => load()} />
            )}

            {appt.serviceType === "pathology" && (
              <Section
                title="Result sheets"
                right={!TERMINAL.has(appt.status) && (
                  <Button size="sm" loading={busy === "sync"} onClick={syncSheets} title="Create any missing sheets for the ordered items">Sync sheets</Button>
                )}
              >
                {sheets.length === 0 ? (
                  <span className="lab-muted">No result sheets. Items need a linked test definition — use Sync sheets after fixing the catalogue.</span>
                ) : (
                  <table className="lab-lines">
                    <thead><tr><th>Test</th><th>Status</th><th>Flags</th><th>Entered</th><th>Verified</th><th /></tr></thead>
                    <tbody>
                      {sheets.map((s) => {
                        const t = templates[s.templateId];
                        return (
                          <tr key={s._id}>
                            <td>
                              <b>{t?.name || s.templateSnapshot?.name || "Test"}</b>{t?.shortName ? <span className="lab-muted"> · {t.shortName}</span> : null}
                              <div className="lab-row" style={{ gap: 6, fontSize: 11 }}>
                                {s.specimenLabel && <span className="lab-muted" style={{ fontFamily: "ui-monospace, Menlo, monospace" }}>{s.specimenLabel}</span>}
                                {s.sampleRejected && <Badge kind="rejected">Specimen rejected</Badge>}
                              </div>
                            </td>
                            <td><Badge kind={s.status}>{RESULT_STATUS_LABEL[s.status] || s.status}</Badge></td>
                            <td>{s.hasCritical ? <Badge kind="CANCELLED">Critical</Badge> : s.hasAbnormal ? <Badge kind="IN_PROCESS">Abnormal</Badge> : <span className="lab-muted">—</span>}</td>
                            <td className="lab-muted">{s.enteredBy?.name || "—"}</td>
                            <td className="lab-muted">{s.verifiedBy?.name || "—"}</td>
                            <td><Button size="xs" variant={s.status === "PUBLISHED" ? "ghost" : "navy"} onClick={() => setSheet(s._id)}>{s.status === "PUBLISHED" ? "View" : s.status === "VERIFIED" ? "Publish" : "Enter"}</Button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </Section>
            )}

            <div className="lab-grid cols-2">
              <Section title="Reports">
                {appt.reports?.length ? (
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>
                    {appt.reports.map((r) => (
                      <li key={r.recordId}>{r.typeOfRecord === "RadiologyRecord" ? "Radiology" : "Pathology"} record <code className="lab-muted">{r.recordId}</code></li>
                    ))}
                  </ul>
                ) : (
                  <span className="lab-muted">
                    {appt.status === "COMPLETED" ? "No report attached."
                      : appt.serviceType === "pathology" ? "Reports appear here as result sheets are published."
                      : "Reports appear here as scans are reported and published."}
                  </span>
                )}
                {appt.status === "REPORT_READY" && <div className="lab-muted" style={{ marginTop: 8 }}>Reports are published. <b>Mark completed</b> once handed over to the patient.</div>}
              </Section>

              <Section
                title="Billing"
                right={
                  canBill ? <Button size="sm" variant="primary" loading={busy === "invoice"} onClick={createInvoice}>Create invoice</Button>
                  : canPay ? <Button size="sm" variant="primary" onClick={() => setPayOpen(true)}>Record payment</Button>
                  : null
                }
              >
                {invoice ? (
                  <dl className="lab-kv">
                    <dt>Invoice</dt><dd>{invoice.invoiceNumber} <Badge kind={invoice.status?.split(" ")[0]}>{invoice.status}</Badge></dd>
                    <dt>Total</dt><dd>{money(invoice.grandTotal)}</dd>
                    <dt>Paid</dt><dd>{money(invoice.totalPaidAmount || 0)}</dd>
                    <dt>Outstanding</dt><dd><b>{money(invoice.outstanding ?? invoice.grandTotal - (invoice.totalPaidAmount || 0))}</b></dd>
                  </dl>
                ) : appt.invoiceId ? (
                  <span className="lab-muted">Invoice {appt.invoiceId} (could not load)</span>
                ) : (
                  <span className="lab-muted">Not billed yet. Quote: <b>{money(appt.amount?.total)}</b></span>
                )}
              </Section>
            </div>

            <Section title="History">
              <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
                {[...(appt.statusHistory || [])].reverse().map((h, i) => (
                  <div key={i} className="lab-row">
                    <StatusBadge status={h.status} />
                    <span className="lab-muted">{fmtDateTime(h.at)} · {h.byName || "—"}{h.note ? ` · ${h.note}` : ""}</span>
                  </div>
                ))}
                {appt.cancellation?.reason && <div className="lab-danger-text">Cancelled: {appt.cancellation.reason}</div>}
              </div>
            </Section>
          </>
        )}
      </Modal>

      <ReasonModal
        open={!!reasonFor}
        title={reasonFor === "CANCELLED" ? "Cancel booking" : `Mark ${STATUS_LABEL[reasonFor] || ""}`}
        onClose={() => setReasonFor(null)}
        busy={!!busy}
        onSubmit={(reason) => move(reasonFor, reason)}
      />
      <AppointmentForm open={editOpen} onClose={() => setEditOpen(false)} appointment={appt} onSaved={applied} />
      <PaymentModal open={payOpen} onClose={() => setPayOpen(false)} invoice={invoice} onSaved={() => load()} />
      <ResultSheet id={sheet} open={!!sheet} onClose={() => setSheet(null)} onChanged={sheetChanged} />
    </>
  );
};

const ItemsTable = ({ appt }) => (
  <>
    <table className="lab-lines">
      <thead><tr><th>Item</th><th className="num">Qty</th><th className="num">Line</th></tr></thead>
      <tbody>
        {(appt.items || []).map((it, i) => (
          <tr key={i}>
            <td>{it.name} <span className="lab-muted">· {cap(it.kind)}{it.discount ? ` · ${it.discount}% off` : ""}</span></td>
            <td className="num">{it.qty}</td>
            <td className="num">{money(it.lineTotal)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <div className="lab-total grand" style={{ marginTop: 6 }}><span>Quote total</span><span>{money(appt.amount?.total)}</span></div>
  </>
);

export default AppointmentDetail;
